import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import middleware from "../Api/Middleware";
import PageTemplate from "../Templates/PageTemplate";
import { BackButton, ButtonBlue, ButtonDarkBlue } from "../HouseStyle/Components/Buttons";
import { BorderedDropDown } from "../HouseStyle/Components/DropDowns";
import CustomerComponent from "./Components/CustomerComponent";
import ToastSuccess from "../Toasts/ToastSuccess";
import ToastError from "../Toasts/ToastError";
import ToastInfo from "../Toasts/ToastInfo";
import ToastWarning from "../Toasts/ToastWarning";
import ToolTip from "./Components/ToolTipComponent";
import ItemNoteModalComponent from "./Components/ItemNoteModalComponent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faX, faSearch } from "@fortawesome/free-solid-svg-icons";
import { hourglass } from "ldrs";
import { formatCurrency, parseCurrency } from "../../utils";
import "./Orders.css";
import "../Table/Table.css";
import { AddressKinds } from "../Enums/addressKinds";

const MAX_NOTE_LENGTH = 5000;
const MAX_REFERENCE_LENGTH = 500;

const OrderForm = () => {
    // Register hourglass loader
    hourglass.register();

    // Navigation and params
    const navigate = useNavigate();
    const { order_number } = useParams();
    const [searchParams] = useSearchParams();
    const referredCustomerId = searchParams.get('u');
    const isEditMode = !!order_number;

    // Loading states
    const [loading, setLoading] = useState(isEditMode ? true : false);
    const [loadingCustomer, setLoadingCustomer] = useState(false);

    // Customer info
    const [customer, setCustomer] = useState({});
    const [addresses, setAddresses] = useState([]);
    const [phoneNumbers, setPhoneNumbers] = useState([]);
    const [emails, setEmails] = useState([]);
    const [deliveryAddress, setDeliveryAddress] = useState(null);
    const [invoiceAddress, setInvoiceAddress] = useState(null);

    // Order info
    const [order, setOrder] = useState({});
    const [reference, setReference] = useState('');
    const [orderInfo, setOrderInfo] = useState({
        note: '',
        noteError: ''
    });

    // Sales info
    const [allSaleschannels, setAllSaleschannels] = useState([]);
    const [salesChannel, setSalesChannel] = useState();
    const [orderProcessing, setOrderProcessing] = useState(isEditMode ? null : "2");
    const [status, setStatus] = useState(isEditMode ? null : "0");

    // Order items and discounts
    const [orderItems, setOrderItems] = useState([]);
    const [productSearch, setProductSearch] = useState('');
    const [searchedProducts, setSearchedProducts] = useState([]);
    const [productsIsLoading, setProductsIsLoading] = useState(false);
    const [productsHasSearched, setProductsHasSearched] = useState(false);
    const [orderDiscount, setOrderDiscount] = useState(null);
    const [totalDiscount, setTotalDiscount] = useState(0);
    const [totalExclTax, setTotalExclTax] = useState(0);
    const [totalInclTax, setTotalInclTax] = useState(0);
    const [maxAmount, setMaxAmount] = useState({});
    const [minAmount, setMinAmount] = useState({});

    // Shipping information
    const [shippingCost, setShippingCost] = useState(0.00);
    const [selectedShippingMethod, setSelectedShippingMethod] = useState(null);

    // Total and tax
    const [subtotal, setSubtotal] = useState(0);
    const [tax, setTax] = useState(0);
    const [activeSaleTypeVerzend, setActiveSaleTypeVerzend] = useState(1);

    // Modals and ui states
    const dropdownRef = useRef(null);
    const [itemNoteModalVisible, setItemNoteModalVisible] = useState(false);
    const [itemNoteProduct, setItemNoteProduct] = useState(null);

    // Fetch initial data
    const fetchInitialData = async () => {
        const [saleChannelsResponse] = await Promise.all([
            middleware.get(`saleschannels`),
        ]);

        setAllSaleschannels(saleChannelsResponse.data.data);
        setSalesChannel(saleChannelsResponse.data.data[0].sales_channel_id); // Set the default salesChannel
    };

    // Fetch existing order data for edit mode
    const fetchOrderData = async () => {
        try {
            setLoading(true);
            const orderRes = await middleware.get(`/orders/fullorder?order_number=${order_number}`);
            const salesChannelsRes = await middleware.get(`/saleschannels`);

            let orderData = orderRes.data.data || orderRes.data;
            if (!orderData) {
                ToastError(`Deze bestelling bestaat niet.`);
                navigate(`/bestellingen`);
                return;
            }

            setOrder(orderData);

            // Set customer based on internal_id
            if (orderData.internal_id.startsWith('C')) {
                setCustomer(orderData.client_consumer || orderData.consumer);
            } else {
                setCustomer(orderData.client_business || orderData.business);
            }

            const unprocessedProducts = orderData.orders_sold_products.filter(product => product.amount - product.items_processed > 0)
                .map(product => ({
                    ...product,
                    amount: product.amount - product.items_processed
                }));
            
            if (unprocessedProducts.length === 0) {
                ToastInfo('Deze bestelling kan niet gewijzigd worden. Alle producten zijn reeds verwerkt.');
                navigate(`/bestellingen/overzicht/${order_number}`);
                return;
            }

            // Set order details
            setOrderProcessing(orderData.order_status);
            setAllSaleschannels(salesChannelsRes.data.data);
            setSalesChannel(orderData.sales_channel_id);
            setSelectedShippingMethod(orderData.shipment_method.toString());
            setStatus(orderData.delivery_status);
            setOrderInfo({ ...orderInfo, note: orderData.note });
            setActiveSaleTypeVerzend(orderData.sale_unit + 1);
            setOrderDiscount(orderData.sale_amount);
            setShippingCost(orderData.delivery_cost);
            setReference(orderData.reference || '');

            // Set addresses
            setAddresses(orderData.client_addresses || []);
            setEmails(orderData.client_emails || []);
            setPhoneNumbers(orderData.client_phonenumbers || []);

            // Set delivery and invoice addresses
            if (orderData.delivery_address) {
                setDeliveryAddress(orderData.delivery_address);
            }
            if (orderData.invoice_address) {
                setInvoiceAddress(orderData.invoice_address);
            }

            // Convert sold products to orderItems format
            const convertedItems = (orderData.orders_sold_products || []).map(item => ({
                // IDs
                id: item.product_id,
                product_id: item.product_id,
                sold_product_id: item.sold_product_id,

                // Basic info
                name: item.product_name,
                product_name: item.product_name,
                product_description: item.product_description,

                // Prices
                price: parseFloat(item.price_incl_tax),
                price_incl_tax: parseFloat(item.price_incl_tax),
                price_excl_tax: parseFloat(item.price_excl_tax),
                sale_price: item.sale_price_incl_tax ? parseFloat(item.sale_price_incl_tax) : null,
                sale_price_incl_tax: item.sale_price_incl_tax ? parseFloat(item.sale_price_incl_tax) : null,
                sale_price_excl_tax: item.sale_price_excl_tax ? parseFloat(item.sale_price_excl_tax) : null,

                // Quantity and stock
                quantity: item.amount,
                amount: item.amount,
                stock: item.stock?.commercial_quantity || 0,
                items_processed: item.items_processed || 0,
                items_credited: item.items_credited || 0,

                // Discount
                discount: item.sale_amount,
                sale_amount: item.sale_amount,
                discountInput: item.sale_amount ? item.sale_amount.toString().replace('.', ',') : null,
                discountType: item.sale_unit === 0 ? '€' : '%',
                sale_unit: item.sale_unit,

                // Additional properties
                color: item.color,
                size: item.size
            }));

            setOrderItems(convertedItems);

            // Fetch customer data
            if (orderData.internal_id) {
                await fetchCustomerData(orderData.internal_id);
            }

        } catch (error) {
            console.error("Error fetching order data:", error);
            ToastError("Fout bij ophalen van ordergegevens");
            navigate(`/bestellingen`);
        } finally {
            setLoading(false);
        }
    };

    // Get referred customer (for new orders)
    const getReferredCustomer = async (internal_id) => {
        try {
            if (customer?.internal_id == undefined && internal_id != undefined) {
                if (Array.from(internal_id)[0] == "C") {
                    const customerResponse = await middleware.get(`consumers?internal_id=${internal_id}`);
                    setCustomer(customerResponse.data.data[0]);
                } else if (Array.from(internal_id)[0] == "B") {
                    const customerResponse = await middleware.get(`businesses?internal_id=${internal_id}`);
                    setCustomer(customerResponse.data.data[0]);
                } else return;
                await fetchCustomerData(internal_id);
            }
        } catch (err) {
            console.error(err);
        }
    };

    // Fetch customer data
    const fetchCustomerData = async (internal_id) => {
        try {
            setLoadingCustomer(true);
            const [
                addressResponse,
                emailResponse,
                phoneNumberResponse,
                countryCodesResponse
            ] = await Promise.all([
                middleware.get(`addresses?internal_id=${internal_id}`),
                middleware.get(`emails?internal_id=${internal_id}`),
                middleware.get(`phonenumbers?internal_id=${internal_id}`),
                middleware.get(`countryCodes`),
            ]);

            if (addressResponse.data.data.length > 0) {
                addressResponse.data.data.forEach((address) => {
                    const countryCode = countryCodesResponse.data.data.find(code => code.country_code_id === address.country_code_id);
                    address.country = countryCode;
                    if ([AddressKinds.DELIVERY_ADDRESS, AddressKinds.DELIVERY_AND_INVOICE_ADDRESS].includes(address.address_kind)) {
                        setDeliveryAddress(address);
                    }

                    if ([AddressKinds.INVOICE_ADDRESS, AddressKinds.DELIVERY_AND_INVOICE_ADDRESS].includes(address.address_kind)) {
                        setInvoiceAddress(address);
                    }
                });
            }

            setAddresses(addressResponse.data.data);
            setEmails(emailResponse.data.data);
            setPhoneNumbers(phoneNumberResponse.data.data);
        } catch (error) {
            console.error("Error fetching customer data:", error);
        } finally {
            setLoadingCustomer(false);
        }
    };

    // Initialize component
    useEffect(() => {
        if (isEditMode) {
            fetchOrderData();
        } else {
            getReferredCustomer(referredCustomerId);
            fetchInitialData();
        }
    }, []);

    // Fetch products based on search
    const debouncedFetchProducts = useCallback(
        debounce(async (searchTerm) => {
            try {
                setProductsIsLoading(true);
                setProductsHasSearched(true);
                const response = await middleware.get(`/sp/productsTableAll?page=1&page_size=10&LIKE=true&include_variations=true&searchTerm=${searchTerm}&noSearch=["product_id","type","image_path","incl_tax","excl_tax","sale_incl_tax","sale_excl_tax","supplier","gender","discount","buy_in_price","stock","categories","brand","created_at","status"]`);
                const products = response.data.data || [];

                setSearchedProducts(products);
            } catch (error) {
                console.error("Error fetching products:", error);
            } finally {
                setProductsIsLoading(false);
            }
        }, 300),
        []
    );

    useEffect(() => {
        if (productSearch) {
            debouncedFetchProducts(productSearch);
        } else {
            setSearchedProducts([]);
            setProductsHasSearched(false);
        }
    }, [productSearch, debouncedFetchProducts]);

    // Handle customer selection
    const handleCustomerSelection = async (selectedCustomer) => {
        setCustomer(selectedCustomer);
        await fetchCustomerData(selectedCustomer.internal_id);
    };

    // Handle product selection
    const handleProductSelection = (product) => {
        const stockQuantity = parseInt(product.stock) || 0;

        const existingItem = orderItems.find(item => item.product_id === product.product_id);
        if (existingItem) {
            updateQuantity(product.product_id, existingItem.quantity + 1);
        } else {
            let discount = null;
            if (product.sale_incl_tax) {
                discount = parseCurrency(product.incl_tax) - parseCurrency(product.sale_incl_tax);
            }

            const newItem = {
                // IDs
                id: product.product_id,
                product_id: product.product_id,

                // Basic info
                name: product.product_name,
                product_name: product.product_name,

                // Prices
                price: parseFloat(product.incl_tax),
                price_incl_tax: parseFloat(product.incl_tax),
                price_excl_tax: parseFloat(product.excl_tax),
                sale_price: product.sale_incl_tax ? parseFloat(product.sale_incl_tax) : null,
                sale_price_incl_tax: product.sale_incl_tax ? parseFloat(product.sale_incl_tax) : null,
                sale_price_excl_tax: product.sale_excl_tax ? parseFloat(product.sale_excl_tax) : null,

                // Quantity and stock
                quantity: 1,
                amount: 1,
                stock: stockQuantity,

                // Discount
                discount: discount,
                sale_amount: discount || 0,
                discountInput: discount ? (parseInt(discount) === discount ? discount : discount.toFixed(2)) : null,
                discountType: '€',
                sale_unit: 0
            };

            setOrderItems(prevItems => [...prevItems, newItem]);
            updateQuantity(product.product_id, 1);
        }

        setProductSearch('');
        setSearchedProducts([]);
        setProductsHasSearched(false);
    };

    // Update quantity
    const updateQuantity = (id, newQuantity) => {
        const numericValue = parseFloat(newQuantity);
        if (!isNaN(numericValue) && numericValue > 0) {
            setOrderItems((prevItems) =>
                prevItems.map((item) => {
                    if (item.product_id === id) {
                        // Check minimum for edit mode
                        if (isEditMode && item.items_processed && numericValue <= (item.items_processed + item.items_credited)) {
                            setMinAmount((prev) => ({ ...prev, [id]: true }));
                            return { ...item, quantity: item.items_processed, amount: item.items_processed };
                        } else {
                            setMinAmount((prev) => ({ ...prev, [id]: false }));
                        }

                        // Check maximum stock
                        const stockLimit = isEditMode && item.stock?.commercial_quantity
                            ? item.stock.commercial_quantity
                            : item.stock;

                        if (numericValue > stockLimit) {
                            if (!maxAmount[id]) {
                                ToastWarning(`Pas op! Commerciele voorraad (${stockLimit}) producten overschreden.`, `max_stock_${id}`);
                            }
                            setMaxAmount((prev) => ({ ...prev, [id]: true }));
                        } else {
                            setMaxAmount((prev) => ({ ...prev, [id]: false }));
                        }

                        return {
                            ...item,
                            quantity: numericValue,
                            amount: numericValue
                        };
                    }
                    return item;
                })
            );
        }
    };

    // Update discount
    const updateDiscount = (id, newDiscount, newDiscountType) => {
        setOrderItems(prevItems =>
            prevItems.map(item => {
                if (item.product_id === id) {
                    let discountValue = parseCurrency(newDiscount);
                    const basePrice = item.sale_price_incl_tax || item.price_incl_tax || item.price;

                    if (newDiscountType === '€' || newDiscountType === 0) {
                        discountValue = Math.min(discountValue, basePrice);
                    } else {
                        discountValue = Math.min(discountValue, 100);
                    }

                    return {
                        ...item,
                        discountType: newDiscountType === 0 ? '€' : (newDiscountType === 1 ? '%' : newDiscountType),
                        sale_unit: newDiscountType === '€' ? 0 : (newDiscountType === '%' ? 1 : newDiscountType),
                        discount: discountValue,
                        sale_amount: discountValue,
                        discountInput: newDiscount.toString().replace('.', ','),
                    };
                }
                return item;
            })
        );
    };

    // Handle discount blur
    const handleDiscountBlur = (id, value, discountType) => {
        setOrderItems((prevItems) =>
            prevItems.map((item) => {
                if (item.product_id === id) {
                    let clampedValue = parseCurrency(value);
                    const basePrice = item.sale_price_incl_tax || item.price_incl_tax || item.price;

                    if (discountType === "€" || discountType === 0) {
                        clampedValue = Math.max(0, Math.min(clampedValue, basePrice));
                    } else {
                        clampedValue = Math.max(0, Math.min(clampedValue, 100));
                    }

                    if (parseInt(clampedValue) !== clampedValue) {
                        clampedValue = clampedValue.toFixed(2);
                    }

                    return {
                        ...item,
                        discountType: discountType === 0 ? '€' : (discountType === 1 ? '%' : discountType),
                        sale_unit: discountType === '€' ? 0 : (discountType === '%' ? 1 : discountType),
                        discount: clampedValue,
                        sale_amount: clampedValue,
                        discountInput: clampedValue === 0 ? '' : clampedValue.toString().replace('.', ','),
                    };
                }
                return item;
            })
        );
    };

    // Calculate discounted price
    function calculateDiscountedPrice(item) {
        const basePrice = item.price_incl_tax || item.price;
        const discountType = item.sale_unit === 0 ? '€' : '%';

        if (discountType === "€") {
            if (item.sale_amount >= basePrice) {
                return 0;
            }
            return Math.max(0, basePrice - item.sale_amount);
        } else {
            if (item.sale_amount >= 100) {
                return 0;
            }
            return Math.max(0, basePrice * (1 - item.sale_amount / 100));
        }
    }

    // Calculate row total
    function calculateRowTotal(item) {
        const discountedPrice = calculateDiscountedPrice(item);
        return discountedPrice * (item.amount || item.quantity);
    }

    // Render discount cell
    const renderDiscountCell = (item) => (
        <div className="d-flex align-items-center justify-content-center">
            <div className="d-flex">
                <button
                    type="button"
                    className={`euroSaleButton p-2 fw-semibold ${item.sale_unit === 0 ? "activeSaleType" : ""}`}
                    onClick={() => handleDiscountBlur(item.product_id, item.sale_amount, 0)}
                >
                    €
                </button>
                <button
                    type="button"
                    className={`percentageSaleButton p-2 fw-semibold ${item.sale_unit === 1 ? "activeSaleType" : ""}`}
                    onClick={() => handleDiscountBlur(item.product_id, item.sale_amount, 1)}
                >
                    %
                </button>
            </div>
            <input
                className="p-2 mx-2 inputSaleOrder text-center fw-semibold"
                placeholder="0"
                step="any"
                min={0}
                value={item.discountInput || ''}
                onChange={(e) => updateDiscount(item.product_id, e.target.value, item.sale_unit)}
                onBlur={(e) => handleDiscountBlur(item.product_id, e.target.value, item.sale_unit)}
            />
        </div>
    );

    // Update total
    const updateTotal = (type, value, isShipping = false) => {
        if (subtotal === 0) {
            setOrderDiscount(0);
            setShippingCost(0);
            return;
        }

        const numericValue = parseFloat(value);

        if (isShipping) {
            if (selectedShippingMethod === "0" || selectedShippingMethod === "pickup") {
                setShippingCost(0);
                return;
            }

            if (type === "€") {
                setShippingCost(numericValue);
            } else if (type === "%") {
                const percentValue = Math.min(numericValue, 100);
                const newShippingCost = (subtotal * percentValue) / 100;
                setShippingCost(newShippingCost);
            }
        } else {
            if (type === "€") {
                const maxDiscount = subtotal;

                let newPrice = Math.min(numericValue, maxDiscount);
                if (isNaN(newPrice)) {
                    setOrderDiscount('');
                } else {
                    setOrderDiscount(newPrice);
                }
                setActiveSaleTypeVerzend(1);
            } else if (type === "%") {
                let newPrice = Math.min(numericValue, 100);
                if (isNaN(newPrice)) {
                    setOrderDiscount('');
                } else {
                    setOrderDiscount(newPrice);
                }
                setActiveSaleTypeVerzend(2);
            }
        }

        calculateTotals();
    };

    // Calculate total product discounts
    const calculateTotalProductDiscounts = () => {
        return orderItems.reduce((sum, item) => {
            const basePrice = item.price_incl_tax || item.price;
            const originalTotal = basePrice * (item.amount || item.quantity);
            const discountedTotal = calculateRowTotal(item);
            return sum + (originalTotal - discountedTotal);
        }, 0);
    };

    // Handle shipping cost change
    const handleShippingCostChange = (value) => {
        if (value === "") {
            setShippingCost("");
            return;
        }
        const numericValue = parseFloat(value);
        if (!isNaN(numericValue) && numericValue >= 0) {
            setShippingCost(numericValue);
        }
        calculateTotals();
    };

    // Handle order discount blur
    const handleOrderDiscountBlur = (value) => {
        const numericValue = parseFloat(value);
        if (!isNaN(numericValue) && numericValue >= 0) {
            if (activeSaleTypeVerzend === 1) {
                setOrderDiscount(Math.max(0, Math.min(numericValue, subtotal)));
            } else {
                setOrderDiscount(Math.max(0, Math.min(numericValue, 100)));
            }
        } else {
            setOrderDiscount(null);
        }
        calculateTotals();
    };

    // Remove item
    const removeItem = (id) => {
        const orderItem = orderItems.find(item => item.product_id === id);

        if (isEditMode && (orderItem.items_processed > 0 || orderItem.items_credited > 0)) {
            ToastInfo(`Je kan "${orderItem.product_name}" niet verwijderen omdat dit product al is verwerkt of gecrediteerd.`, `already_processed_${id}`);
            return;
        }

        setOrderItems((prevItems) => prevItems.filter((item) => item.product_id !== id));

        setMaxAmount((prevMaxAmount) => {
            const updatedMaxAmount = { ...prevMaxAmount };
            delete updatedMaxAmount[id];
            return updatedMaxAmount;
        });

        setMinAmount((prevMinAmount) => {
            const updatedMinAmount = { ...prevMinAmount };
            delete updatedMinAmount[id];
            return updatedMinAmount;
        });

        calculateTotals();
    };

    // Handle note change
    const handleNoteChange = (e) => {
        const value = e.target.value;
        if (value.length <= MAX_NOTE_LENGTH) {
            setOrderInfo({
                ...orderInfo,
                note: value,
                noteError: "",
            });
        } else {
            setOrderInfo({
                ...orderInfo,
                noteError: `Notitie mag niet langer zijn dan ${MAX_NOTE_LENGTH} tekens`,
            });
            ToastError(`Notitie mag niet langer zijn dan ${MAX_NOTE_LENGTH} tekens`, 'max_note_length');
        }
    };

    // Handle reference change
    const handleReferenceChange = (e) => {
        const value = e.target.value;
        if (value.length <= MAX_REFERENCE_LENGTH) {
            setReference(value);
        } else {
            ToastError(`Referentie mag niet langer zijn dan ${MAX_REFERENCE_LENGTH} tekens`, 'max_reference_length');
        }
    };

    // Calculate totals
    const calculateTotals = useCallback(() => {
        const productTotal = orderItems.reduce((sum, item) => {
            const discountedPrice = calculateDiscountedPrice(item);
            return sum + discountedPrice * (item.amount || item.quantity);
        }, 0);

        const extraDiscountAmount =
            activeSaleTypeVerzend === 1
                ? Math.min(parseFloat(orderDiscount) || 0, productTotal)
                : (productTotal * Math.min(parseFloat(orderDiscount) || 0, 100)) / 100;

        const amountAfterExtraDiscount = Math.max(
            0,
            productTotal - extraDiscountAmount
        );

        let shippingCostValue = selectedShippingMethod === "0" || selectedShippingMethod === "pickup"
            ? 0
            : parseCurrency(shippingCost);

        const subtotalAmount = amountAfterExtraDiscount + shippingCostValue;

        const exclBTW = subtotalAmount / 1.21;
        const btwAmount = subtotalAmount - exclBTW;

        setSubtotal(productTotal);
        setTotalDiscount(extraDiscountAmount);
        setTotalExclTax(exclBTW);
        setTotalInclTax(subtotalAmount);
        setTax(btwAmount);
    }, [
        orderItems,
        orderDiscount,
        activeSaleTypeVerzend,
        shippingCost,
        selectedShippingMethod,
        isEditMode
    ]);

    // Update totals when dependencies change
    useEffect(() => {
        calculateTotals();
    }, [
        orderItems,
        orderDiscount,
        activeSaleTypeVerzend,
        shippingCost,
        selectedShippingMethod,
        calculateTotals
    ]);

    // Open item note modal
    const openModal = (item) => {
        setItemNoteProduct(item);
        setItemNoteModalVisible(true);
    };

    // Set order regel (for item note modal)
    const setOrderRegel = (productId, name, description) => {
        setOrderItems((prev) =>
            prev.map((e) =>
                e.product_id === productId || e.sold_product_id === productId
                    ? { ...e, product_name: name, name: name, product_description: description, product_description_2: description }
                    : e
            )
        );
    };

    // Submit order
    const submitOrder = async (overrideOrderStatus = null) => {
        // Validations
        if (orderInfo?.note?.length > MAX_NOTE_LENGTH) {
            ToastError(`Notitie mag niet langer zijn dan ${MAX_NOTE_LENGTH} tekens`);
            return;
        }

        if (reference.length > MAX_REFERENCE_LENGTH) {
            ToastError(`Referentie mag niet langer zijn dan ${MAX_REFERENCE_LENGTH} tekens`);
            return;
        }

        if (orderItems.length === 0) {
            ToastError("Voeg producten toe aan de bestelling");
            return;
        }

        if (!customer.internal_id) {
            ToastError("Selecteer eerst een klant");
            return;
        }

        if (!selectedShippingMethod && overrideOrderStatus !== "draft") {
          ToastError("Kies een verzendmethode");
          return;
        }

        if (!salesChannel) {
            ToastError("Selecteer een verkoopkanaal");
            return;
        }

        if (orderProcessing === null || status === null) {
            ToastError("Vul zowel de bestelstatus als de verzendstatus in");
            return;
        }

        const orderData = {
            internal_id: customer.internal_id,
            sales_channel_id: parseInt(salesChannel),
            order_status: parseInt(orderProcessing),
            note: orderInfo.note,
            total_price_incl_tax: totalInclTax,
            total_price_excl_tax: totalExclTax,
            sale_amount: isNaN(orderDiscount) || orderDiscount == null || orderDiscount === "" ? 0 : orderDiscount,
            sale_unit: activeSaleTypeVerzend - 1,
            amount_paid: 0,
            shipment_method: selectedShippingMethod === 'pickup' ? '0' : (selectedShippingMethod === '0' ? '0' : '1'),
            delivery_status: parseInt(status),
            delivery_cost: shippingCost,
            delivery_tax: parseCurrency((shippingCost / 1.21) * 0.21),
            delivery_address_id: deliveryAddress?.address_id || order.delivery_address_id,
            invoice_address_id: invoiceAddress?.address_id || order.invoice_address_id,
            email_address_id: orderInfo.email_address_id,
            phone_number_id: orderInfo.phone_number_id,
            order_sold_products: orderItems.map((item) => ({
                product_id: item.product_id,
                amount: item.amount || item.quantity,
                product_name: item.product_name || item.name,
                product_description: item.product_description,
                incl_tax: item.price_incl_tax || item.price,
                excl_tax: item.price_excl_tax,
                sale_incl_tax: item.sale_price_incl_tax || item.sale_price,
                sale_excl_tax: item.sale_price_excl_tax,
                sale_amount: item.sale_amount || item.discount || 0,
                sale_unit: item.sale_unit || (item.discountType === "%" ? 1 : 0),
            })),
            reference: reference,
            invoice_note: orderInfo.note,
        };

        // Add order_id for edit mode
        if (isEditMode) {
            orderData.order_id = order.order_id;
        } else {
            orderData.order_status = overrideOrderStatus === 'draft' ? 1 : orderData.order_status;
        }

        try {
            setLoading(true);

            let orderResponse;
            if (isEditMode) {
                await middleware.put("orders", orderData);
                ToastSuccess(`Bestelling ${order.order_number} aangepast`);
                navigate(`/bestellingen/overzicht/${order.order_number}`);
                return;
            } else {
                orderResponse = await middleware.post("orders", orderData);
                const newOrder = orderResponse.data.data;
                if (overrideOrderStatus === 'draft') {
                    ToastSuccess(`Bestelling ${newOrder.order_number} succesvol opgeslagen als concept!`, 'order_created', 2000);
                } else {
                    ToastSuccess(`Bestelling ${newOrder.order_number} succesvol aangemaakt!`, 'order_created', 2000);
                }

                navigate('/bestellingen/overzicht/' + newOrder.order_number);
            }
        } catch (error) {
            console.error("Error occurred:", error);
            console.error(
                "Error details:",
                error.response ? error.response.data : "No response data"
            );

            const errorMessage = error.response?.data?.message || error.message;
            ToastError(`Fout bij ${isEditMode ? 'aanpassen' : 'aanmaken'} bestelling: ${errorMessage || 'Onbekende fout opgetreden'}`);
        }
        setLoading(false);
    };

    // Get sales channels array for dropdown
    const salesChannelsArray = allSaleschannels.filter(e=>e.status===1).map(e => ({
        title: e.sales_channel_name,
        value: e.sales_channel_id.toString()
    }));

    return (
        <PageTemplate navbarTitle="Order HUB" pageIsLoading={loading}>
            <ItemNoteModalComponent
                itemNoteModalVisible={itemNoteModalVisible}
                setItemNoteModalVisible={setItemNoteModalVisible}
                setOrderRegel={setOrderRegel}
                product={itemNoteProduct}
            />

            <div className="NewOrder w-100">
                <form className="row">
                    {/* Header */}
                    <div className="d-flex flex-row pb-3 justify-content-between me-4">
                        <BackButton
                            text={isEditMode ? `# ${order_number}` : `Alle bestellingen`}
                            disabled={false}
                            onClickFunction={() => navigate(isEditMode ? `/bestellingen/overzicht/${order_number}` : `/bestellingen`)}
                        />

                        <div className="d-flex gap-3">
                            {!isEditMode && (
                                <ButtonBlue
                                    text={"Sla op als concept"}
                                    onClickFunction={() => submitOrder('draft')}
                                />
                            )}
                            <ButtonDarkBlue
                                text={isEditMode ? "Opslaan" : "Maak order aan"}
                                onClickFunction={() => submitOrder()}
                            />
                        </div>
                    </div>

                    {/* Products Section */}
                    <div className="col-12 col-lg-8 order-1 mb-4">
                        <div className="bg-white rounded-4">
                            <div className="p-4">
                                <div className="fs-5 mb-4 fw-bold">Producten</div>

                                {/* Search with dropdown */}
                                <div className="position-relative mb-4" ref={dropdownRef}>
                                    <div className="position-relative">
                                        <input
                                            type="text"
                                            value={productSearch || ""}
                                            onChange={(e) => setProductSearch(e.target.value)}
                                            onBlur={(e) => {
                                                const relatedTarget = e.relatedTarget;
                                                if (!dropdownRef.current.contains(relatedTarget)) {
                                                    setSearchedProducts([]);
                                                    setProductsHasSearched(false);
                                                }
                                            }}
                                            placeholder="Product zoeken… (naam of barcode)"
                                            className="form-control rounded-pill"
                                            style={{
                                                padding: "10px 40px 10px 20px",
                                                border: "1px solid #E5E7EB",
                                            }}
                                        />
                                        <FontAwesomeIcon
                                            icon={faSearch}
                                            className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted"
                                        />
                                    </div>

                                    {/* Products Dropdown */}
                                    {(searchedProducts.length > 0 || (productsHasSearched && productSearch)) && (
                                        <div
                                            className="position-absolute w-100 bg-white border rounded mt-1 shadow-lg overflow-hidden"
                                            style={{
                                                zIndex: 1050,
                                                maxHeight: "300px",
                                                overflowY: "auto",
                                            }}
                                        >
                                            {productsIsLoading ? (
                                                <div className="text-center p-3">
                                                    <div
                                                        className="spinner-border text-primary"
                                                        role="status"
                                                    >
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                </div>
                                            ) : searchedProducts.length > 0 ? (
                                                searchedProducts.map((product) => (
                                                    <button
                                                        key={product.product_id}
                                                        className="w-100 text-start px-3 py-2 border-0 bg-transparent hover:bg-gray-50"
                                                        onClick={() => handleProductSelection(product)}
                                                        style={{
                                                            borderBottom: '1px solid #E5E7EB',
                                                            transition: 'background-color 0.2s'
                                                        }}
                                                    >
                                                        <div className="fw-medium">{product.product_name}</div>
                                                        <div className="text-muted small d-flex justify-content-between">
                                                            {product.incl_tax ? product.sale_incl_tax ? (
                                                                <div>
                                                                    <span><s>{formatCurrency(product.incl_tax)}</s> | {formatCurrency(product.sale_incl_tax)}</span>
                                                                </div>
                                                            ) : <span>{formatCurrency(product.incl_tax)}</span> : <span>€ -</span>}
                                                            <span>Commerciele voorraad: {product.stock ?? '-'} | Magazijn voorraad: {product.stock ?? '-'}</span>
                                                        </div>
                                                    </button>
                                                ))
                                            ) : (
                                                <div className="text-center p-4">
                                                    <div className="text-muted">
                                                        <FontAwesomeIcon
                                                            icon={faSearch}
                                                            className="mb-2"
                                                            style={{ fontSize: '2rem', opacity: 0.5 }}
                                                        />
                                                        <p className="mb-0">Geen producten gevonden voor "{productSearch}"</p>
                                                        <small>Probeer een andere zoekterm</small>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Selected Products Table */}
                                {orderItems.length > 0 && (
                                    <div className="table-responsive">
                                        <table className="table">
                                            <thead className="">
                                                <tr>
                                                    <th className="text-center">Aantal</th>
                                                    <th>Product</th>
                                                    <th className="text-center">Prijs incl. BTW</th>
                                                    <th className="text-center">Korting p. st.</th>
                                                    <th className="text-center">Prijs p. st.</th>
                                                    <th className="text-center">Totaal</th>
                                                    <th className="text-center"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {orderItems.map((item) => (
                                                    <tr
                                                        key={item.product_id}
                                                        className="align-middle"
                                                    >
                                                        <td>
                                                            <div className="d-flex align-items-center justify-content-center">
                                                                <button
                                                                    type="button"
                                                                    className="btn border rounded-0 rounded-start d-flex align-items-center justify-content-center"
                                                                    disabled={item.quantity <= 1 || !!minAmount[item.product_id]}
                                                                    onClick={() =>
                                                                        updateQuantity(
                                                                            item.product_id,
                                                                            Math.max(1, (item.amount || item.quantity) - 1)
                                                                        )
                                                                    }
                                                                >
                                                                    -
                                                                </button>
                                                                <input
                                                                    type="number"
                                                                    className="form-control rounded-0 text-center"
                                                                    style={{ width: "60px" }}
                                                                    value={item.amount ?? item.quantity ?? ''}
                                                                    disabled={!!minAmount[item.product_id]}
                                                                    onChange={(e) =>
                                                                        updateQuantity(
                                                                            item.product_id,
                                                                            Math.max(1, parseInt(e.target.value) || 0)
                                                                        )
                                                                    }
                                                                    min="1"
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="btn border rounded-0 rounded-end d-flex align-items-center justify-content-center"
                                                                    onClick={() =>
                                                                        updateQuantity(item.product_id, (item.amount || item.quantity) + 1)
                                                                    }
                                                                >
                                                                    +
                                                                </button>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div>
                                                                <span
                                                                    onClick={() => openModal(item)}
                                                                    className="text-info product-description-width text-decoration-none"
                                                                    style={{ cursor: 'pointer' }}
                                                                >
                                                                    {item.product_name || item.name}
                                                                    {item.color ? ` ${item.color}` : ''}
                                                                    {item.size ? ` ${item.size}` : ''}
                                                                </span>
                                                                <div className="text-muted small product-description-width">
                                                                    {(item.product_description_2 || item.product_description)?.length > 50
                                                                        ? (item.product_description_2 || item.product_description).substring(0, 50) + '...'
                                                                        : (item.product_description_2 || item.product_description)}
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="text-center">
                                                            {item.sale_price === 0 || item.sale_price === null || isNaN(item.sale_price) ? (
                                                                <span>{formatCurrency(item.price_incl_tax || item.price)}</span>
                                                            ) : (
                                                                <>
                                                                    <s className="d-block">
                                                                        {formatCurrency(item.price_incl_tax || item.price)}
                                                                    </s>
                                                                    <span className="d-block">
                                                                        {formatCurrency(item.sale_price_incl_tax || item.sale_price)}
                                                                    </span>
                                                                </>
                                                            )}
                                                        </td>

                                                        <td>{renderDiscountCell(item)}</td>

                                                        <td className="text-center">{formatCurrency(calculateDiscountedPrice(item))}</td>
                                                        <td className="text-center">
                                                            {formatCurrency(calculateRowTotal(item))}
                                                        </td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm rounded-circle"
                                                                onClick={() => removeItem(item.product_id)}
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={faX}
                                                                    style={{ color: "#000000" }}
                                                                />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        {/* Totals at bottom of products table */}
                                        <div className="d-flex justify-content-end mt-4 me-5">
                                            <div style={{ width: "250px" }}>
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span className="fw-bold">Totaal producten</span>
                                                    <span>
                                                        {formatCurrency(orderItems.reduce((sum, item) => {
                                                            const basePrice = item.price_incl_tax || item.price;
                                                            return sum + basePrice * (item.amount || item.quantity);
                                                        }, 0))}
                                                    </span>
                                                </div>
                                                <div className="d-flex justify-content-between pb-2 mb-2 bottom-line-overzicht">
                                                    <span className="fw-bold">Totaal korting</span>
                                                    <span className="text-danger">
                                                        -{" "}
                                                        {formatCurrency(Math.abs(calculateTotalProductDiscounts()))}
                                                    </span>
                                                </div>
                                                <div className="d-flex justify-content-between fw-bold">
                                                    <span>Totaal</span>
                                                    <span>
                                                        {formatCurrency(
                                                            orderItems
                                                                .reduce((sum, item) => {
                                                                    const discountedPrice =
                                                                        calculateDiscountedPrice(item);
                                                                    return sum + discountedPrice * (item.amount || item.quantity);
                                                                }, 0)
                                                        )}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Notes Section */}
                                <div className="row mt-4">
                                    <div className="col-md-6 mb-4">
                                        <div className="fw-bold mb-2">Opmerking op factuur</div>
                                        <textarea
                                            className="form-control rounded-4"
                                            rows="4"
                                            placeholder="Opmerking op factuur"
                                            value={orderInfo.note ?? ''}
                                            onChange={handleNoteChange}
                                        ></textarea>
                                    </div>
                                    <div className="col-md-6 mb-4">
                                        <div className="fw-bold mb-2">Extra informatie</div>
                                        <textarea
                                            className="form-control rounded-4"
                                            rows="4"
                                            placeholder="Referentie op factuur"
                                            value={reference ?? ''}
                                            onChange={handleReferenceChange}
                                        ></textarea>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="right-side-ordernew col-12 col-lg-4 order-2 row">
                        {/* Customer Card */}
                        <div className="pe-0 mb-4">
                            {loadingCustomer ? (
                                <div className="whiteBox p-4 py-5 d-flex justify-content-center align-items-center">
                                    <l-hourglass
                                        size="40"
                                        bg-opacity="0.1"
                                        speed="1"
                                        color="gray"
                                    ></l-hourglass>
                                </div>
                            ) : (
                                <CustomerComponent
                                    mode="add"
                                    editable={!isEditMode}
                                    customer={customer}
                                    phoneNumbers={phoneNumbers}
                                    addresses={addresses}
                                    setAddresses={setAddresses}
                                    setDeliveryAddress={setDeliveryAddress}
                                    setPhones={setPhoneNumbers}
                                    setEmails={setEmails}
                                    order={orderInfo}
                                    setOrder={setOrderInfo}
                                    emails={emails}
                                    onSelectCustomer={handleCustomerSelection}
                                    addCustomer={!isEditMode}
                                    loading={loadingCustomer}
                                />
                            )}
                        </div>

                        {/* Order Overview Card */}
                        <div className="pe-0 mb-4">
                            <div className="whiteBox p-4">
                                <h5 className="mb-4 fw-bold">Bestelling overzicht</h5>

                                {/* Total Section */}
                                <div className="bottom-line-overzicht mt-3 mb-3">
                                    <div className="d-flex justify-content-between">
                                        <p className="fw-bold">Totaal</p>
                                        <p className="fw-bold">{formatCurrency(subtotal)}</p>
                                    </div>

                                    {/* Extra Discount Section */}
                                    <div className="d-flex justify-content-between mb-3">
                                        <div className="d-flex align-items-center">
                                            Extra korting
                                        </div>
                                        <div className="d-flex buttonswitch">
                                            <div className="d-flex align-items-center">
                                                <div className="d-flex">
                                                    <button
                                                        type="button"
                                                        className={`euroSaleButton p-2 fw-semibold ${activeSaleTypeVerzend === 1 ? "activeSaleType" : ""}`}
                                                        onClick={() => updateTotal("€", orderDiscount, false)}
                                                        disabled={subtotal === 0}
                                                    >€</button>
                                                    <button
                                                        type="button"
                                                        className={`percentageSaleButton p-2 fw-semibold ${activeSaleTypeVerzend === 2 ? "activeSaleType" : ""}`}
                                                        onClick={() => updateTotal("%", orderDiscount, false)}
                                                        disabled={subtotal === 0}
                                                    >%</button>
                                                </div>
                                                <input
                                                    className="p-2 mx-2 inputSaleOrder text-center fw-semibold"
                                                    placeholder="0"
                                                    type="number"
                                                    step="any"
                                                    value={orderDiscount === '0.00' ? '' : orderDiscount}
                                                    onChange={(e) => setOrderDiscount(e.target.value)}
                                                    onBlur={(e) => handleOrderDiscountBlur(e.target.value)}
                                                />
                                                <div className="ms-3">
                                                    {formatCurrency((activeSaleTypeVerzend === 1
                                                        ? Math.min(orderDiscount || 0, subtotal)
                                                        : (subtotal * Math.min(orderDiscount || 0, 100) / 100)))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Bedrag na extra korting Section */}
                                <div className="bottom-line-overzicht pb-2">
                                    <div className="d-flex justify-content-between fw-bold">
                                        <p className="mb-0">Bedrag na extra korting</p>
                                        <p className="mb-0">{formatCurrency(Math.max(0, subtotal - (activeSaleTypeVerzend === 1
                                            ? Math.min(orderDiscount || 0, subtotal)
                                            : (subtotal * Math.min(orderDiscount || 0, 100) / 100))))}</p>
                                    </div>

                                    {/* Shipping Section */}
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <p className="mb-0">Verzendmethode</p>
                                            <select
                                                className="form-select-shipping rounded-3"
                                                style={{ width: 'auto' }}
                                                value={selectedShippingMethod ?? ''}
                                                onChange={(e) => {
                                                    setSelectedShippingMethod(e.target.value);
                                                    if (e.target.value === 'pickup' || e.target.value === '0') {
                                                        setShippingCost(0);
                                                    } else if (e.target.value === 'shipping' || e.target.value === '1') {
                                                        setShippingCost(6.95);
                                                    }
                                                }}
                                            >
                                                <option value="" hidden>Selecteer</option>
                                                <option value={isEditMode ? "0" : "pickup"}>Ophalen</option>
                                                <option value={isEditMode ? "1" : "shipping"}>Verzenden</option>
                                            </select>
                                        </div>
                                        {selectedShippingMethod ? (
                                            <div className="d-flex align-items-center">
                                                <span className="me-2">€</span>
                                                <input
                                                    className="shipping-cost px-3 py-1 rounded"
                                                    placeholder="0"
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    value={selectedShippingMethod === 'pickup' || selectedShippingMethod === '0' ? '0' : shippingCost}
                                                    onChange={(e) => handleShippingCostChange(e.target.value)}
                                                    disabled={selectedShippingMethod === 'pickup' || selectedShippingMethod === '0'}
                                                />
                                            </div>
                                        ) : ""}
                                    </div>
                                </div>

                                {/* Subtotal and BTW Section */}
                                <div className="pt-3 bottom-line-overzicht pb-3">
                                    <div className="d-flex justify-content-between fw-bold mb-2">
                                        <h4 className="fs-5 mb-0 fw-bold">Subtotaal</h4>
                                        <h5 className="fs-5 fw-bold">{formatCurrency(totalInclTax)}</h5>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2">
                                        <div>Totaal excl. BTW</div>
                                        <div>{formatCurrency(totalExclTax)}</div>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <div>BTW</div>
                                        <div className="d-flex gap-5">
                                            <div>NL 21%</div>
                                            <div>{formatCurrency(tax)}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Total Including VAT */}
                                <div className="d-flex justify-content-between pt-3">
                                    <h5 className="fw-bold mb-0">Totaal incl. BTW</h5>
                                    <h5 className="fs-5 fw-bold">{formatCurrency(totalInclTax)}</h5>
                                </div>
                            </div>
                        </div>

                        {/* Status Card */}
                        <div className="pe-0">
                            <div className="whiteBox p-4 ">
                                <h5 className="fw-bold mb-4">Status</h5>

                                <div className="mb-3">
                                    <label className="form-label">Bestelling status</label>
                                    <ToolTip message={"De status van de bestelling. Deze word automatisch bijgewerkt."} className="pl-1 ms-2" />
                                    <BorderedDropDown
                                        placeholder="Bestelling status"
                                        selectedValue={orderProcessing?.toString()}
                                        setSelectedValue={setOrderProcessing}
                                        options={[
                                            { title: "Geannuleerd", value: '0' },
                                            { title: "Concept", value: '1' },
                                            { title: "Nieuw", value: '2' },
                                            { title: "Deels in behandeling", value: '3' },
                                            { title: "In behandeling", value: '4' },
                                            { title: "Voltooid", value: '5' },
                                        ]}
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Verkooplocatie</label>
                                    <ToolTip message={isEditMode ? "De verkooplocatie" : "Selecteer de verkooplocatie. Deze kan na het aanmaken van de bestelling niet meer aangepast worden."} className="pl-1 ms-2" />
                                    <BorderedDropDown
                                        disabled={isEditMode}
                                        placeholder="Verkooplocatie"
                                        selectedValue={salesChannel?.toString()}
                                        setSelectedValue={setSalesChannel}
                                        options={salesChannelsArray}
                                    />
                                </div>

                                <div>
                                    <label className="form-label">Verzendstatus</label>
                                    <ToolTip message={"Verzend status van de order. Deze word automatisch bijgewerkt."} className="pl-1 ms-2" />
                                    <BorderedDropDown
                                        placeholder="Verzendstatus"
                                        selectedValue={status?.toString()}
                                        setSelectedValue={setStatus}
                                        options={[
                                            { title: "Nog niet verzonden", value: "0" },
                                            { title: "Concept", value: "1" },
                                            { title: "Deels verzonden", value: "2" },
                                            { title: "Verzonden", value: "3" },
                                            { title: "Afgeleverd", value: "4" }
                                        ]}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </PageTemplate>
    );
};

export default OrderForm;